import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import LoopCTAs from '../components/LoopCTAs'
import TrackedCTA from '../components/TrackedCTA'
import ImageCarousel from '../components/ImageCarousel'
import FloatingBackground from '../components/FloatingBackground'
import Seo from '../components/Seo'
import { FadeIn, MagneticCTA } from '../components/Motion'
import { trackCTA } from '../utils/analytics'
import {
  BETA_CTA_TEXT,
  WAITLIST_CTA_TEXT,
  EMBED_BETA_HREF,
  VOE_WAITLIST_HREF,
} from '../config/cta'

const comparisonData = [
  {
    method: 'Survey Response',
    cost: '$0.10 - $1',
    tradeoff: 'Quantitative only—what, not why',
    closes: 'No',
    highlighted: false,
  },
  {
    method: 'PrismScope Conversation',
    cost: '$5 - $16',
    tradeoff: 'Qualitative depth at scale',
    closes: 'Yes — in hours',
    highlighted: true,
  },
  {
    method: 'Human Interview',
    cost: '$150 - $200',
    tradeoff: 'Same depth, expensive & slow',
    closes: 'Yes, but slow',
    highlighted: false,
  },
  {
    method: 'Focus Group (per person)',
    cost: '$200 - $300',
    tradeoff: 'Group dynamics, logistics nightmare',
    closes: 'Yes, but logistics',
    highlighted: false,
  },
]

const innerLoop = [
  { label: 'Listen', body: 'Conversational survey — adapts, follows up, probes for the why.' },
  { label: 'Distribute', body: 'Push the conversation to email, SMS, QR, or in-product so the people who don\'t normally respond actually do.' },
  { label: 'Synthesize', body: 'Themes, sentiment, notable quotes, cross-stakeholder patterns.' },
  { label: 'Reflect', body: "Growth retro: went well · could have gone better · what's next." },
  { label: 'Act', body: 'Actionable results + executive summary, ready to ship.' },
]

const outerLoops = [
  { title: 'Events', outer: 'Plan → Execute → Retro → Improve', body: 'Multi-stakeholder events: organizers, attendees, sponsors, vendors, staff.', href: '/feedback-loops/event' },
  { title: 'Validation', outer: 'Hypothesize → Test → Learn → Pivot', body: 'PMF, value prop, and concept validation. Founders, studios, innovation arms.', href: '/feedback-loops/validation' },
  { title: 'Product', outer: 'Build → Ship → Test → Refine', body: 'Rapid iteration. In-product embed (beta) for feedback at the point of use.', href: '/feedback-loops/product' },
  { title: 'Customer lifecycle', outer: 'Acquire → Onboard → Serve → Retain → Win-back', body: 'NPS follow-up, churn diagnostics, onboarding, win-back.', href: '/feedback-loops/customer' },
  { title: 'Client service', outer: 'Deliver → Listen → Improve → Re-engage', body: 'Long-term client relationships. Professional services and agencies.', href: '/feedback-loops/service' },
  {
    title: 'Employees',
    outer: 'Hire → Engage → Develop → Retain',
    body: 'Continuous employee listening, built on the same conversational engine. Coming soon.',
    href: '#voe-waitlist',
    demoted: true,
  },
]

export default function Home() {
  return (
    <div>
      <Seo
        rawTitle
        title="PrismScope — Feedback to action."
        description="Close the feedback loop between the people you serve and the decisions you make. Conversational surveys that surface novel perspectives."
        path="/"
      />

      {/* Hero Section */}
      <section className="relative bg-white py-20 md:py-32 overflow-hidden">
        <FloatingBackground />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Eyebrow tag */}
          <FadeIn>
            <div className="flex justify-center lg:justify-start mb-8">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border-2 border-intelligence-900 bg-white text-xs font-semibold uppercase tracking-wider text-intelligence-900 shadow-[3px_3px_0_0_#3b82f6]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3b82f6] animate-pulse" />
                Conversational survey platform · live
              </span>
            </div>
          </FadeIn>

          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 text-center lg:text-left">
              <FadeIn delay={0.05}>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.95]">
                  Stop guessing{' '}
                  <span className="relative inline-block">
                    <span className="relative z-10 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B] bg-clip-text text-transparent">
                      why
                    </span>
                    <span
                      aria-hidden="true"
                      className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-4 bg-[#8b5cf6]/25 -z-0 -skew-y-1"
                    />
                  </span>
                  <span className="text-[#00008B]">.</span>
                </h1>
              </FadeIn>

              <FadeIn delay={0.15}>
                <p className="text-xl md:text-2xl text-intelligence-700 mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                  Surveys tell you what people rated, never{' '}
                  <span className="font-semibold text-intelligence-900 underline decoration-[#8b5cf6] decoration-4 underline-offset-4">
                    why
                  </span>
                  . PrismScope runs conversational interviews that follow up, probe, and surface the reason the 1–5 scale missed.
                </p>
              </FadeIn>

              <FadeIn delay={0.25}>
                <MagneticCTA strength={0.15}>
                  <LoopCTAs location="home-hero" showMicrocopy />
                </MagneticCTA>
              </FadeIn>

              {/* Founder strip — chunky pill chips, each links to its loop */}
              <FadeIn delay={0.4}>
                <div className="mt-12">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4 text-center lg:text-left">
                    Built for the teams shipping fast
                  </p>
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
                    {[
                      { label: 'Founders', color: '#3b82f6', to: '/feedback-loops/validation' },
                      { label: 'Venture studios', color: '#8b5cf6', to: '/feedback-loops/validation' },
                      { label: 'Event organizers', color: '#00008B', to: '/feedback-loops/event' },
                      { label: 'Product teams', color: '#3b82f6', to: '/feedback-loops/product' },
                    ].map((a) => {
                      const slug = a.label.toLowerCase().replace(/\s+/g, '-')
                      return (
                        <Link
                          key={a.label}
                          to={a.to}
                          onClick={() => trackCTA(a.label, `home-built-for-${slug}`)}
                          className="group inline-flex items-center gap-2 px-4 py-2 border-2 border-intelligence-900 rounded-full bg-white text-base font-semibold text-intelligence-900 shadow-[2px_2px_0_0_rgba(15,23,42,1)] transition-all hover:-translate-y-0.5 hover:shadow-[3px_3px_0_0_rgba(15,23,42,1)] hover:bg-intelligence-50"
                        >
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: a.color }} />
                          {a.label}
                          <ArrowRight className="w-3.5 h-3.5 opacity-0 -ml-1 -mr-0.5 transition-all group-hover:opacity-100 group-hover:ml-0" />
                        </Link>
                      )
                    })}
                  </div>
                </div>
              </FadeIn>
            </div>

            <div className="lg:col-span-5">
              <FadeIn delay={0.2}>
                <div className="relative group">
                  {/* Chunky offset shadow frame — logo navy */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 rounded-2xl bg-[#00008B] translate-x-3 translate-y-3 transition-transform duration-300 group-hover:translate-x-4 group-hover:translate-y-4"
                  />
                  <div className="relative overflow-hidden rounded-2xl border-2 border-intelligence-900 bg-white">
                    <img
                      src="/images/hero/Hero-A-Authenic_Conversation.jpg"
                      alt="Two professionals having an engaged conversation"
                      className="w-full object-cover aspect-[4/3] transition-transform duration-700 group-hover:scale-[1.02]"
                    />
                  </div>
                  {/* Floating stat badge */}
                  <div className="absolute -bottom-4 -left-4 bg-white border-2 border-intelligence-900 rounded-xl px-4 py-3 shadow-[4px_4px_0_0_rgba(15,23,42,1)]">
                    <div className="text-xl font-bold text-intelligence-900">
                      Synthesis in hours
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Buyer stakes — problem statement */}
      <section className="bg-white py-12 md:py-16 border-t border-intelligence-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-2xl bg-[#8b5cf6] translate-x-2 translate-y-2"
              />
              <div className="relative bg-white rounded-2xl border-2 border-intelligence-900 p-8 md:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
                  The stakes
                </p>
                <p className="text-2xl md:text-3xl font-bold text-intelligence-900 leading-tight">
                  Companies make high-stakes decisions — pricing, product, retention — without knowing{' '}
                  <span className="bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B] bg-clip-text text-transparent">
                    why
                  </span>{' '}
                  their customers stay, leave, or buy.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Why surveys fail you — stat strip */}
      <section className="bg-white py-8 md:py-12 border-t border-intelligence-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-6 text-center">
              Why surveys fail you
            </p>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {[
              {
                stat: '~43%',
                body: 'of churned customers never voiced their concerns.',
                source: 'Gartner, 2024 CX Survey',
              },
              {
                stat: '~10–15%',
                body: 'Email survey response rates in 2025, down from ~20–25% in 2019.',
                source: 'Industry benchmarks',
              },
            ].map((s, i) => (
              <FadeIn key={s.source} delay={i * 0.1}>
                <div className="text-center md:text-left">
                  <div className="text-4xl md:text-5xl font-bold text-intelligence-900 mb-2">
                    {s.stat}
                  </div>
                  <p className="text-base md:text-lg text-intelligence-700 mb-2">
                    {s.body}
                  </p>
                  <p className="text-xs text-intelligence-500 italic">
                    {s.source}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Product Preview */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                Live in product
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                See it in action
              </h2>
              <p className="text-lg text-intelligence-600 max-w-2xl mx-auto">
                Real conversations that adapt to every response, uncovering the insights that matter.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <ImageCarousel />
          </FadeIn>
        </div>
      </section>

      {/* The Three Pillars — Depth / Reach / Trust */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                The problem
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                You&apos;re missing the why, missing most of the people, and you can&apos;t verify what you do get.
              </h2>
              <p className="text-lg text-intelligence-600 max-w-3xl mx-auto">
                Three things stand between feedback and a confident decision. PrismScope closes all three.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                kicker: 'Pillar 01',
                title: 'Depth',
                tagline: 'The reason, not the score.',
                gap: 'Surveys tell you what people rated, never why. The comment that would have changed the decision doesn’t fit a 1–5 scale.',
                capability: 'Adaptive conversational interviews follow up, probe, and ask the question the survey couldn’t.',
                proof: 'See it in any event report: session-level reasoned findings, not a satisfaction average.',
                color: '#3b82f6',
              },
              {
                kicker: 'Pillar 02',
                title: 'Reach',
                tagline: 'Hear who you’re missing.',
                gap: 'You hear the loud few. The quiet majority who decide your future never respond, and the stakeholder groups nobody asked often hold the insight.',
                capability: 'Conversations the silent will actually take, distributed in parallel across every audience you should be asking.',
                proof: 'See it in our event campaigns: organizers, attendees, sponsors, vendors, staff — all in one synthesis.',
                color: '#8b5cf6',
              },
              {
                kicker: 'Pillar 03',
                title: 'Trust',
                tagline: 'Proof you can verify.',
                gap: 'AI summaries can be black boxes. You can’t tell if a theme is real or invented, or show your stakeholders the receipts.',
                capability: 'Every theme, sentiment, and quote in a PrismScope report traces back to the respondent and exchange that produced it.',
                proof: 'Click any finding to see the source conversation. No black box.',
                color: '#00008B',
              },
            ].map((pillar, i) => (
              <FadeIn key={pillar.title} delay={i * 0.1}>
                <div className="relative group h-full">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 rounded-xl translate-x-2 translate-y-2 transition-transform duration-300 group-hover:translate-x-3 group-hover:translate-y-3"
                    style={{ backgroundColor: pillar.color }}
                  />
                  <div className="relative h-full bg-white rounded-xl border-2 border-intelligence-900 p-8">
                    <span
                      className="inline-block font-mono text-xs uppercase tracking-widest mb-3 px-2 py-0.5 border border-intelligence-900 rounded"
                      style={{ color: pillar.color }}
                    >
                      {pillar.kicker}
                    </span>
                    <h3 className="text-2xl font-bold text-intelligence-900 mb-1">{pillar.title}</h3>
                    <p className="text-sm font-semibold text-intelligence-600 mb-4">{pillar.tagline}</p>
                    <p className="text-intelligence-700 leading-relaxed mb-3">
                      {pillar.gap}
                    </p>
                    <p className="text-intelligence-700 leading-relaxed mb-3">
                      {pillar.capability}
                    </p>
                    <p className="text-sm text-intelligence-500 leading-relaxed italic">
                      {pillar.proof}
                    </p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* How it works — chunky retro step badges + connector */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                Process
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                How it works
              </h2>
              <p className="text-lg text-intelligence-600">
                Five steps from first interview to acted-on decision.
              </p>
            </div>
          </FadeIn>
          <div className="relative">
            {/* Connector line (desktop only) */}
            <div
              aria-hidden="true"
              className="hidden md:block absolute top-7 left-[10%] right-[10%] h-0.5 border-t-2 border-dashed border-intelligence-300"
            />
            <div className="relative grid md:grid-cols-5 gap-6 md:gap-2">
              {innerLoop.map((step, i) => {
                const colors = ['#3b82f6', '#8b5cf6', '#00008B', '#3b82f6', '#8b5cf6']
                const color = colors[i]
                return (
                  <FadeIn key={step.label} delay={i * 0.08}>
                    <div className="text-center">
                      <div className="relative inline-block mb-4">
                        <div
                          aria-hidden="true"
                          className="absolute inset-0 rounded-lg translate-x-1 translate-y-1"
                          style={{ backgroundColor: color }}
                        />
                        <div className="relative w-14 h-14 bg-white border-2 border-intelligence-900 rounded-lg flex items-center justify-center text-2xl font-bold text-intelligence-900">
                          {i + 1}
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-intelligence-900 mb-2">{step.label}</h3>
                      <p className="text-sm text-intelligence-600 max-w-[14rem] mx-auto">{step.body}</p>
                    </div>
                  </FadeIn>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Flagship: Multi-stakeholder Events */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                Flagship use case
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                One event. The full stakeholder picture. One synthesis.
              </h2>
              <p className="text-lg text-intelligence-600 max-w-3xl mx-auto">
                Most event feedback asks attendees alone. PrismScope runs parallel campaigns
                across organizers, attendees, sponsors, food vendors, and staff — then synthesizes
                across all of them in a single growth retro.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {[
                { label: 'Organizers', color: '#3b82f6' },
                { label: 'Attendees', color: '#8b5cf6' },
                { label: 'Sponsors', color: '#00008B' },
                { label: 'Food & beverage', color: '#3b82f6' },
                { label: 'Venue staff', color: '#8b5cf6' },
                { label: 'Speakers', color: '#00008B' },
              ].map((s) => (
                <span
                  key={s.label}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-intelligence-900 rounded-full bg-white text-base font-semibold text-intelligence-900 shadow-[2px_2px_0_0_rgba(15,23,42,1)]"
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.label}
                </span>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="text-center">
              <div className="relative inline-block group">
                <div
                  aria-hidden="true"
                  className="absolute inset-0 rounded-lg bg-[#00008B] translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2"
                />
                <Link
                  to="/feedback-loops/event"
                  onClick={() => trackCTA('Plan a multi-stakeholder event loop', 'home-events-flagship')}
                  className="relative inline-flex items-center px-8 py-4 text-lg font-bold text-white bg-intelligence-900 border-2 border-intelligence-900 rounded-lg"
                >
                  Plan a multi-stakeholder event loop <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Comparison Table — retro outlined version */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                Method comparison
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">The Value</h2>
              <p className="text-lg text-intelligence-600">Qualitative depth at survey prices.</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-xl bg-[#3b82f6] translate-x-2 translate-y-2"
              />
              <div className="relative bg-white rounded-xl border-2 border-intelligence-900 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-intelligence-900 bg-intelligence-900 text-white">
                        <th className="text-left py-4 px-6 font-mono text-xs uppercase tracking-widest">Method</th>
                        <th className="text-left py-4 px-6 font-mono text-xs uppercase tracking-widest">Cost</th>
                        <th className="text-left py-4 px-6 font-mono text-xs uppercase tracking-widest">Trade-off</th>
                        <th className="text-left py-4 px-6 font-mono text-xs uppercase tracking-widest">Gets you the why?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.map((row) => (
                        <tr
                          key={row.method}
                          className={`border-b-2 border-intelligence-200 last:border-b-0 ${
                            row.highlighted ? 'bg-[#8b5cf6]/10' : ''
                          }`}
                        >
                          <td
                            className={`py-4 px-6 ${
                              row.highlighted ? 'text-intelligence-900 font-bold' : 'text-intelligence-900'
                            }`}
                          >
                            {row.highlighted && (
                              <span className="inline-block w-2 h-2 rounded-full bg-[#8b5cf6] mr-2 align-middle" />
                            )}
                            {row.method}
                          </td>
                          <td
                            className={`py-4 px-6 ${
                              row.highlighted ? 'text-[#8b5cf6] font-bold' : 'text-intelligence-600'
                            }`}
                          >
                            {row.cost}
                          </td>
                          <td
                            className={`py-4 px-6 ${
                              row.highlighted ? 'text-intelligence-700' : 'text-intelligence-500'
                            }`}
                          >
                            {row.tradeoff}
                          </td>
                          <td
                            className={`py-4 px-6 ${
                              row.highlighted ? 'text-[#8b5cf6] font-bold' : 'text-intelligence-500'
                            }`}
                          >
                            {row.closes}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Outer Loops we plug into — chunky cards with rotating shadow colors */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                One platform, every use case
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                Built for the decision you&apos;re making.
              </h2>
              <p className="text-lg text-intelligence-600 max-w-2xl mx-auto">
                Pick the one that matches your iteration.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {outerLoops.map((loop, i) => {
              const shadows = ['#00008B', '#8b5cf6', '#3b82f6', '#00008B', '#8b5cf6', '#3b82f6']
              const shadowColor = loop.demoted ? '#94a3b8' : shadows[i]
              const cardClasses = loop.demoted
                ? 'relative block h-full bg-intelligence-50 rounded-xl border-2 border-intelligence-300 p-6'
                : 'relative block h-full bg-white rounded-xl border-2 border-intelligence-900 p-6'
              const titleColor = loop.demoted ? 'text-intelligence-700' : 'text-intelligence-900'
              const bodyColor = loop.demoted ? 'text-intelligence-500' : 'text-intelligence-600'
              const ctaLabel = loop.demoted ? 'Get notified' : 'Explore loop'
              const trackingSlug = loop.title.toLowerCase().replace(/\s+/g, '-')
              const trackingName = loop.demoted ? `${loop.title} — waitlist` : loop.title
              return (
                <FadeIn key={loop.title} delay={i * 0.05}>
                  <div className="relative group h-full">
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 rounded-xl translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2.5 group-hover:translate-y-2.5"
                      style={{ backgroundColor: shadowColor }}
                    />
                    <Link
                      to={loop.href}
                      onClick={() => trackCTA(trackingName, `home-outer-loops-${trackingSlug}`)}
                      className={cardClasses}
                    >
                      {loop.demoted && (
                        <span className="inline-flex items-center gap-2 px-2 py-0.5 mb-3 border-2 border-intelligence-300 bg-white text-intelligence-700 text-[10px] font-bold uppercase tracking-widest rounded-full">
                          Coming soon
                        </span>
                      )}
                      <h3 className={`text-xl font-bold ${titleColor} mb-2`}>{loop.title}</h3>
                      <p
                        className="font-mono text-[11px] uppercase tracking-widest mb-3"
                        style={{ color: shadowColor }}
                      >
                        {loop.outer}
                      </p>
                      <p className={`${bodyColor} mb-4`}>{loop.body}</p>
                      <span className={`font-semibold inline-flex items-center ${titleColor} group-hover:gap-2 gap-1 transition-all`}>
                        {ctaLabel} <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  </div>
                </FadeIn>
              )
            })}
          </div>
        </div>
      </section>

      {/* In-product embed beta — chunky card-in-section */}
      <section className="bg-accent-50 py-16 md:py-20 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-2xl bg-[#8b5cf6] translate-x-2 translate-y-2"
              />
              <div className="relative bg-white rounded-2xl border-2 border-intelligence-900 p-10 md:p-14 text-center">
                <span className="inline-flex items-center gap-2 px-3 py-1 mb-5 border-2 border-intelligence-900 bg-[#8b5cf6] text-white text-xs font-bold uppercase tracking-widest rounded-full shadow-[2px_2px_0_0_rgba(15,23,42,1)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Beta
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                  Put the conversation where your users already are.
                </h2>
                <p className="text-lg text-intelligence-700 mb-8 max-w-2xl mx-auto">
                  Embed PrismScope directly inside your product via iframe or URL link.
                  Trigger conversations at decision points, friction states, and success moments.
                  Themes flow back into your existing PrismScope workspace.
                </p>
                <div className="relative inline-block group">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 rounded-lg bg-intelligence-900 translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2"
                  />
                  <TrackedCTA
                    href={EMBED_BETA_HREF}
                    location="home-embed-beta"
                    ctaName={BETA_CTA_TEXT}
                    className="relative inline-flex items-center px-8 py-4 text-lg font-bold text-white bg-[#8b5cf6] border-2 border-intelligence-900 rounded-lg"
                  >
                    {BETA_CTA_TEXT} <ArrowRight className="ml-2 w-5 h-5" />
                  </TrackedCTA>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Voice of Employee — coming soon (canonical waitlist) */}
      <section
        id="voe-waitlist"
        className="bg-white py-10 md:py-12 border-t-2 border-intelligence-900 scroll-mt-20"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <span className="inline-flex items-center gap-2 px-3 py-1 mb-4 border-2 border-intelligence-900 bg-intelligence-100 text-intelligence-900 text-xs font-bold uppercase tracking-widest rounded-full shadow-[2px_2px_0_0_rgba(15,23,42,1)]">
              Coming soon
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-intelligence-900 mb-3">
              Voice of Employee
            </h2>
            <p className="text-base text-intelligence-600 mb-6 max-w-xl mx-auto">
              A dedicated product for continuous employee listening, built on the same conversational engine.
            </p>
            <TrackedCTA
              href={VOE_WAITLIST_HREF}
              location="home-voe-waitlist"
              ctaName={WAITLIST_CTA_TEXT}
              className="inline-flex items-center px-6 py-3 text-base font-bold text-intelligence-900 bg-white border-2 border-intelligence-900 rounded-lg shadow-[3px_3px_0_0_rgba(15,23,42,1)] transition-transform hover:-translate-y-0.5 hover:translate-x-0.5 hover:shadow-[4px_4px_0_0_rgba(15,23,42,1)]"
            >
              {WAITLIST_CTA_TEXT} <ArrowRight className="ml-2 w-4 h-4" />
            </TrackedCTA>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-intelligence-900 py-20 md:py-28 relative overflow-hidden">
        {/* Decorative offset accent strips */}
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B]" />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-[#00008B] via-[#8b5cf6] to-[#3b82f6]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-400 mb-3">
              Your move
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-2 leading-tight">
              Ready to know{' '}
              <span className="bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-white bg-clip-text text-transparent">
                why
              </span>
              ?
            </h2>
            <p className="text-lg text-intelligence-400 mb-10">
              More of the voices surveys miss. Synthesis in hours.
            </p>
            <LoopCTAs location="home-final" align="center" />
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
